import * as React from 'react'
import { Stack, Grid, Typography, Box, Tooltip, Chip } from '@mui/material'
import { FileRow } from './FileRow'

/* Pulse animation for total-size bar */
// const pulseAnimation = {
//   animation: 'pulse 1.2s ease-in-out infinite',
//   '@keyframes pulse': {
//     '0%': { opacity: 1 },
//     '50%': { opacity: 0.4 },
//     '100%': { opacity: 1 },
//   },
// }

interface FileListProps {
  files: File[]
  uploadedFiles: Set<string>
  getFileSignature: (f: File) => string
  onRemove: (file: File) => void
  totalSelectedSize: number
  uploadedTotalSize: number
  formatBytes: (b: number) => string
  totalLimit: number
  MAX_FILES: number
  restoredFileMeta: Array<{ name: string; size: number }>
}

export function FileList({
  files,
  uploadedFiles,
  getFileSignature,
  onRemove,
  totalSelectedSize,
  uploadedTotalSize,
  formatBytes,
  totalLimit,
  MAX_FILES,
  restoredFileMeta,
}: FileListProps) {
  if (files.length === 0 && restoredFileMeta.length === 0) return null

  // ✅ FIX: define overLimit before using it
  const overLimit = totalSelectedSize > totalLimit

  return (
    <Stack spacing={1} mt={1}>
      {/* <Typography variant='subtitle1' fontWeight={600}>
        Files:
      </Typography> */}

      {/* <Divider /> */}

      <Grid container spacing={1}>
        {/* Restored (previously uploaded) files */}
        {restoredFileMeta.map((fileMeta) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={`restored-${fileMeta.name}`}>
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                backgroundColor: '#f5f5f5',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                border: '1px solid #ddd',
              }}
            >
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: 1,
                  backgroundColor: '#ff6b6b',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '0.75rem',
                  flexShrink: 0,
                }}
              >
                PDF
              </Box>
              <Box sx={{ overflow: 'hidden', flexGrow: 1 }}>
                <Tooltip title={fileMeta.name} arrow>
                  <Typography
                    noWrap
                    sx={{ fontWeight: 600, fontSize: '0.9rem', cursor: 'default' }}
                  >
                    {fileMeta.name}
                  </Typography>
                </Tooltip>
                <Typography variant='body2' sx={{ color: 'gray', fontSize: '0.75rem' }}>
                  {formatBytes(fileMeta.size)}
                </Typography>
              </Box>

              {/* Uploaded chip */}
              <Chip
                label='Uploaded'
                size='small'
                color='success'
                sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, flexShrink: 0 }}
              />
            </Box>
          </Grid>
        ))}

        {/* Newly selected files */}
        {files.map((file) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={`${file.name}-${file.lastModified}`}>
            <FileRow
              file={file}
              uploadedFiles={uploadedFiles}
              getFileSignature={getFileSignature}
              onRemove={onRemove}
            />
          </Grid>
        ))}
      </Grid>

      {/* NEW — Display total size */}
      <Box mt={1}>
        <Typography
          variant='body2'
          fontWeight={600}
          sx={{ color: overLimit ? 'error.main' : 'text.primary' }}
        >
          Total Selected Size: {formatBytes(totalSelectedSize)} / {formatBytes(totalLimit)}
        </Typography>

        {/* Remaining size */}
        {!overLimit && (
          <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
            Remaining: {formatBytes(totalLimit - totalSelectedSize)}
          </Typography>
        )}

        {/* 🚨 File Count Warning */}
        {files.length > MAX_FILES && (
          <Box
            sx={{
              mt: 1,
              p: 1,
              borderRadius: 1,
              backgroundColor: '#fff4d1',
              border: '1px solid #ffdd99',
            }}
          >
            <Typography variant='body2' color='warning.main' fontWeight={600}>
              You selected {files.length} files, but the maximum allowed is {MAX_FILES}. Please
              remove some files.
            </Typography>
          </Box>
        )}

        {/* 🚨 Warning Banner */}
        {overLimit && (
          <Box
            sx={{
              mt: 1,
              p: 1,
              borderRadius: 1,
              backgroundColor: '#ffe2e2',
              border: '1px solid #ffb3b3',
            }}
          >
            <Typography variant='body2' color='error.main' fontWeight={600}>
              Total file size exceeds the {formatBytes(totalLimit)} limit. Please remove some files.
            </Typography>
          </Box>
        )}

        {/* Success message for restored files — hidden when new files are selected */}
        {restoredFileMeta.length > 0 && files.length === 0 && (
          <Box
            sx={{
              mt: 1,
              p: 1,
              backgroundColor: '#e2f3e0',
              borderRadius: 1,
            }}
          >
            <Typography color='success.main' fontWeight={600}>
              Files uploaded successfully.
            </Typography>
          </Box>
        )}
      </Box>
    </Stack>
  )
}
